import type { Carer, Home, UserRole } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import useHomesList from 'hooks/useHome/useHomes'
import { useInfiniteScrollFetch } from 'hooks/useInfiniteScroll'
import useUsersInfiniteList from 'hooks/useUser/useUserInfiniteList'
import useZones from 'hooks/useZones'
import 'react'
import { type FC, useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { Spinner } from '../../../../components/generic'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import AddTeamMemberModal, {
  type AddTeamMemberProps
} from './AddTeamMemberModal'
import DeleteProfileModal from './DeleteProfileModal'
import {
  TeamListHeaderGroups,
  TeamListItemGroups,
  TeamSideGroupsFilters
} from './index'

const PAGINATION_LIMIT = 50

const StyledRowWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    max-height: max-content;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 2000px;
  height: calc(100vh - 109px);
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    height: calc(100vh - 63px);
  }
`

const StyledScrollerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 210px);
  overflow-y: auto;
  width: 100%;
`
// Its independent page component always true
const GROUP_USER = true

const TeamListGroups: FC = () => {
  const dispatch = useDispatch()

  const queryClient = useQueryClient()

  const handleResetCache = () => {
    queryClient.invalidateQueries({ queryKey: ['users'], exact: false })
  }

  const { homes } = useHomesList({
    params: { isGroupUserManagement: GROUP_USER }
  })

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const { fetchZones } = useZones()

  const { t } = useTranslation()

  const [search, setSearch] = useState<string>('')

  const hasSearch = !!search

  const [roles, setRoles] = useState<UserRole[]>([])

  const [statuses, setStatuses] = useState<string[]>(['active'])

  const [sites, setSites] = useState<string[]>([])

  const [isTeamMemberModalOpen, setIsTeamMemberModalOpen] = useState(false)

  const [modalType, setModalType] =
    useState<AddTeamMemberProps['type']>('addTeamMember')

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [selectedCarer, setSelectedCarer] = useState<Carer | undefined>()

  const canInviteUser = useAbilityAccess([
    { i: 'create', this: 'UserInvitation' }
  ])

  const canDeleteUser = useAbilityAccess([{ i: 'delete', this: 'User' }])

  const getUserStatusQuery = () => {
    if (statuses?.length === 1) {
      return statuses?.[0] === 'active'
    }

    return undefined
  }

  const hasStatusDefined = statuses?.length === 1

  const { isLoading, users, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useUsersInfiniteList({
      params: {
        isCursorPaginationEnabled: true,
        limit: PAGINATION_LIMIT,
        roles,
        ...(hasStatusDefined && { isActive: getUserStatusQuery() }),
        sites,
        ...(hasSearch && {
          searchByName: search
        })
      }
    })

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount
  useEffect(() => {
    handleResetCache()
    if (features?.pro) {
      fetchZones()
    }
  }, [])

  const hasCarers = !!users?.length

  const closeProfileDelete = () => {
    setIsDeleteModalOpen(false)
    handleResetCache()
  }

  const handleDelete = (carer: Carer) => {
    setSelectedCarer(carer)
    setIsDeleteModalOpen(true)
  }

  const closeTeamMemberModal = () => {
    handleResetCache()
    setIsTeamMemberModalOpen(false)
  }

  const openModal = (
    modalType: AddTeamMemberProps['type'] = 'addTeamMember'
  ) => {
    setModalType(modalType)
    setIsTeamMemberModalOpen(true)
  }

  const teamList = () => {
    return (
      <div>
        {users.map((carer: Carer, index) => {
          return (
            <TeamListItemGroups
              firstItem={index === 0}
              key={carer.id}
              carer={carer}
              onDelete={canDeleteUser ? handleDelete : undefined}
              onClick={(e) => {
                e.stopPropagation()
                history.push(`/organisation/team/${carer.id}/profile`)
              }}
              homes={homes}
            />
          )
        })}
        {(isLoading || hasNextPage) && (
          <div ref={infiniteRef}>
            <Spinner style={{ paddingTop: 0 }} />
          </div>
        )}
      </div>
    )
  }

  const handleSubscribePro = () => {
    dispatch(Operations.openProPlanCheckout({}))
  }

  return (
    <StyledMainContainer>
      <TeamListHeaderGroups
        openModal={openModal}
        onSearch={setSearch}
        title={t(`general.team.teamMembers`)}
        searchPlaceholder={t(`general.team.typeToSearch`)}
        buttonTitle={t(`general.team.invite`)}
        buttonDisabled={!canInviteUser}
      />
      <StyledRowWrapper>
        <TeamSideGroupsFilters
          onRoles={setRoles}
          onStatuses={setStatuses}
          statuses={statuses}
          roles={roles}
          features={features}
          clearButtonTitle={t(`general.buttons.clear`)}
          statusTitle={t(`general.team.status`)}
          roleTitle={t(`general.team.role`)}
          viewProTitle={t(`general.team.viewPro`)}
          onSubscribe={handleSubscribePro}
          onDefaultOpened={isDesktop}
          sites={sites}
          onSites={setSites}
          siteTitle={t(`general.team.site`)}
          isGroupUser={GROUP_USER}
        />
        {hasCarers && (
          <>
            {isDesktop && (
              <StyledScrollerDesktop ref={rootRef}>
                {teamList()}
              </StyledScrollerDesktop>
            )}
            {!isDesktop && teamList()}
          </>
        )}
        {!isLoading && !hasCarers && (
          <EmptyState
            title={t(`general.emptyState.noTeamListResponseTitle`)}
            description={t(`general.emptyState.noTeamListResponseDescription`)}
            icon={<NoDataIcon />}
          />
        )}
        {isLoading && hasCarers && <Spinner />}
      </StyledRowWrapper>

      {isTeamMemberModalOpen && (
        <AddTeamMemberModal
          open={isTeamMemberModalOpen}
          onClose={closeTeamMemberModal}
          type={modalType}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteProfileModal
          open={isDeleteModalOpen}
          onClose={closeProfileDelete}
          carer={selectedCarer}
        />
      )}
    </StyledMainContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default TeamListGroups
